import { Area } from '@app/domain/Area'
import { Booking } from '@app/domain/Booking'
import { BookingSource } from '@app/domain/BookingSource'
import { BookingStatusType } from '@app/domain/BookingStatus'
import { LineItem } from '@app/domain/LineItem'
import {
    BookingViewModel,
} from '@app/features/bookings/components/booking-graph/view-models/booking-view.model'
import { Venue } from '@app/domain/Venue'
import { QuestionType } from '@app/domain/Question'
import { ServiceStatus } from '@app/domain/ServiceStatus'

const ORDER_SUMMARY_TOOLTIP_MAX_LENGTH = 50

export class BookingDetailsViewModel implements BookingViewModel {

    public partySize: number
    public statusType: BookingStatusType
    public reasonName: string | null
    public calendarColour: string | null
    public isNew: boolean
    public requiresWheelchairAccess: boolean
    public requiresDogFriendly: boolean
    public requiresPramAccessible: boolean
    public requiresHighchairFriendly: boolean
    public utmSource: string | null
    public utmSourceColor: string | null
    public hasCustomUtmSourceColor: boolean
    public serviceStatus: ServiceStatus | null = null
    public timeZone: string

    constructor(
        public booking: Booking,
        protected area: Area,
        venue: Venue,
        private currentDateTime: Date
    ) {
        this.timeZone = venue.timeZone
        this.partySize = booking.size
        this.statusType = booking.status().type
        const reason = booking.reasonId ? venue.reasonWithId(booking.reasonId) : null
        const event = booking.eventId ? venue.eventWithId(booking.eventId) : null
        this.reasonName = reason?.displayName ?? event?.displayName ?? null
        this.calendarColour = reason?.calendarColour ?? null
        this.isNew = this.isBookingNew()
        this.requiresWheelchairAccess = booking.requiresWheelchairAccess
        this.requiresDogFriendly = booking.requiresDogFriendly
        this.requiresPramAccessible = booking.requiresPramAccessible
        this.requiresHighchairFriendly = booking.requiresHighchairFriendly
        this.utmSource = booking.getUtmSource()
        this.utmSourceColor = this.calculateUtmSourceColor(venue)
        this.hasCustomUtmSourceColor = this.utmSourceColor !== null
        this.serviceStatus = this.statusType === BookingStatusType.Seated
            && venue.serviceStatusIsEnabled(this.booking.serviceStatus)
            ? this.booking.serviceStatus
            : null
    }

    get areaId() {
        return this.area.id
    }

    get tableId() {
        const firstDisplayedTableId = this.area.tablesSortedByDisplayOrder
            .map(table => table.id)
            .find(id => this.booking.tableIds.includes(id))
        if (!firstDisplayedTableId) {
            throw new Error('Booking does not use any tables in this area')
        }
        return firstDisplayedTableId
    }

    get primaryTableName() {
        return this.area.getTableWithId(this.tableId)?.shortFormattedDisplayName ?? ''
    }

    get allTableNames() {
        return this.booking.tableIds
            .map(tableId => this.area.getTableWithId(tableId)?.shortFormattedDisplayName)
            .filter(name => name !== null)
            .join(', ')
    }

    get startDate() {
        return this.booking.start
    }

    get occupiedEndDate() {
        const tableTurnaroundInterval = this.area
            .tableTurnaroundIntervalForPartySize(this.booking.size)
        return this.booking.occupiedEndDate(tableTurnaroundInterval)
    }

    get endDate() {
        return this.booking.end
    }

    get isRunningLate() {
        return this.booking.isRunningLate(this.currentDateTime)
    }

    get name() {
        if (this.booking.isWalkIn()) {
            return 'Walk-in'
        }
        return this.booking.name
    }

    get conditionIconPath() {
        if (this.isRunningLate) {
            return 'assets/late/normal.svg'
        }
        if (this.statusType === BookingStatusType.Booked && this.booking.hasBeenConfirmed()) {
            return 'assets/confirmed/normal.svg'
        }
        if (this.statusType === BookingStatusType.Requested) {
            return 'assets/inbox/small-deep-blue.svg'
        }
        return null
    }

    get conditionName() {
        if (this.isRunningLate) {
            return 'Running Late'
        }
        if (this.statusType === BookingStatusType.Waiting) {
            return 'Waiting at Bar'
        }
        if (this.statusType === BookingStatusType.PartiallySeated) {
            return 'Partially Seated'
        }
        if (this.statusType === BookingStatusType.Seated) {
            return 'Seated'
        }
        if (this.statusType === BookingStatusType.Booked && this.booking.hasBeenConfirmed()) {
            return 'Confirmed'
        }
        if (this.statusType === BookingStatusType.Requested) {
            return 'Requested'
        }
        if (this.statusType === BookingStatusType.PendingPayment) {
            return 'Pending Payment'
        }
        if (this.statusType === BookingStatusType.Finished) {
            return 'Finished'
        }
        return 'Booked'
    }

    get showEmailAddress() {
        return this.booking.emailAddress !== null
            && this.booking.emailAddress!.length > 0
    }

    get emailAddress() {
        return this.booking.emailAddress
    }

    get showPhoneNumber() {
        return this.booking.phoneNumber !== null
            && this.booking.phoneNumber!.length > 0
    }

    get phoneNumber() {
        return this.booking.phoneNumber
    }

    get showHasAcknowledgedEndTime() {
        return this.booking.hasAcknowledgedEndTime()
    }

    get showCustomerNotes() {
        // Salt special occasion question shows graphically as a customer note
        if (this.booking.answers.some(answer => answer.question.id === '40e0f914-da61-4806-8d63-f6a62b5d7eba')) {
            return true
        }
        return this.booking.notes !== null
            && this.booking.notes.length > 0
    }

    get showMerchantNotes() {
        return this.booking.merchantNotes !== null
            && this.booking.merchantNotes.length > 0
    }

    get showCustomerNote() {
        return (this.booking.customer?.note() ?? null) !== null
    }

    get showWheelchair() {
        return this.requiresWheelchairAccess
    }

    get showDogFriendly() {
        return this.booking.requiresDogFriendly
    }

    get showPramAccessible() {
        return this.booking.requiresPramAccessible
    }

    get showHighchairFriendly() {
        return this.booking.requiresHighchairFriendly
    }

    get showPaymentCard() {
        return this.hasCardDetails || this.isPendingCardDetails
    }

    get hasCardDetails() {
        if (!this.booking.cancellation) {
            return false
        }
        return this.booking.cancellation.paymentMethodId !== null
    }

    get isPendingCardDetails() {
        return !!this.booking.pendingCancellationCharge
    }

    get showDeposit() {
        return this.isPendingDeposit || this.isDepositPaid
    }

    get isPendingDeposit() {
        return !!this.booking.pendingDepositCharge
    }

    get isDepositPaid() {
        return !!this.booking.deposit
    }

    get showStatusBar() {
        return true
    }

    get showLockedToTables() {
        return this.booking.lockedToTables
    }

    get source() {
        return this.booking.source
    }

    get customerMessage(): string | null {
        return this.booking.notes
    }

    get merchantNotes(): string | null {
        return this.booking.merchantNotes
    }

    get customerNote(): string | null {
        return this.booking.customer?.note() ?? null
    }

    get expiryDate(): Date | null {
        return this.booking.expiryDate()
    }

    get showVip() {
        return this.booking.customer?.vip ?? false
    }

    get dietaryRestrictions(): string | null {
        const dietaryValues = this.booking.answers
            .filter(answer => answer.question.type === QuestionType.DietaryRestrictions)
            .map(answer => answer.answer)
            .filter(answer => answer !== 'None')
            .flatMap(answer => answer.split(','))
            .map(answer => answer.trim())
            .filter(answer => answer.length > 0)

        const textValues = this.booking.answers
            .filter(answer => answer.question.type === QuestionType.DietaryRestrictionsText)
            .map(answer => answer.answer)
            .filter(answer => answer !== 'None')
            .map(answer => answer.trim())
            .filter(answer => answer.length > 0)

        const customerRequirements = this.booking.customer?.foodDrinkPreferences?.trim()
        const allValues = [...dietaryValues, ...textValues, customerRequirements].filter(Boolean)

        if (allValues.length === 0) {
            return null
        }
        return allValues.join(', ')
    }

    get firstQuestionWithAnswer(): { question: string; answer: string } | null {
        let question: { question: string; answer: string } | null = null
        const textQuestion = this.booking.answers.find(answer => {
            return answer.question.type === QuestionType.Text
        })
        if (textQuestion && !question) {
            question = {
                question: textQuestion.question.text,
                answer: textQuestion.answer
            }
        }
        const multipleChoiceQuestion = this.booking.answers.find(answer => {
            return answer.question.type === QuestionType.Dropdown
        })
        if (multipleChoiceQuestion && !question) {
            question = {
                question: multipleChoiceQuestion.question.text,
                answer: multipleChoiceQuestion.answer
            }
        }
        return question
    }

    get hasPaidOrder(): boolean {
        return this.booking.order?.isPaid ?? false
    }

    get showOrder() {
        return !!this.booking.order
    }

    get orderSummary(): string | null {
        const lineItems = this.booking.order?.lineItems || []
        const summary = lineItems
            .map(lineItem => this.formatOrderLineItemSummary(lineItem))
            .filter((lineItemSummary): lineItemSummary is string => lineItemSummary !== null)
            .join(', ')

        return summary.length > 0 ? summary : null
    }

    get tooltipOrderSummary(): string | null {
        const summary = this.orderSummary
        if (!summary) {
            return null
        }
        if (summary.length <= ORDER_SUMMARY_TOOLTIP_MAX_LENGTH) {
            return summary
        }
        return `${summary.slice(0, ORDER_SUMMARY_TOOLTIP_MAX_LENGTH).trimEnd()}...`
    }

    private formatOrderLineItemSummary(lineItem: LineItem | null | undefined): string | null {
        const quantity = lineItem?.quantity ?? 0
        if (quantity <= 0) {
            return null
        }

        const name = lineItem?.name?.trim() || 'Item'
        return `${name} x${quantity}`
    }

    private calculateUtmSourceColor(venue: Venue): string | null {
        if (!venue || this.source !== BookingSource.Customer) {
            return null
        }
        if (!this.utmSource) {
            return null
        }
        const utmSourceConfig = venue.diaryPreferences.utmSources.find(
            source => source.source === this.utmSource
        )
        return utmSourceConfig?.colour || null
    }

    private isBookingNew(): boolean {
        const isBooked = this.booking.status().type === BookingStatusType.Booked
        if (!isBooked) {
            return false
        }
        const newTimePeriodMinutes = 30
        const dateCreated = this.booking.dateCreated
        const currentDateTime = this.currentDateTime
        return currentDateTime.getTime() - dateCreated.getTime() < newTimePeriodMinutes * 60 * 1000
    }
}
